import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from '@/lib/i18n';
import { isValidAdminPassword } from '../lib/adminPasswordPolicy';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  SectionCard,
  toast,
} from '@/components/ui';
import { adminFetch } from '@/lib/apiClient';
import { errorFromResponse, friendlyMessage } from '@/lib/errors';
import { ApiClientError, idempotencyKeyFor } from '@/lib/http-core';

export { isValidAdminPassword } from '../lib/adminPasswordPolicy';

/**
 * BE 반영 전(엔드포인트 없음)이면 404/405 — 화면에서 "서버 반영 후" 로 안내한다. 단 대상 관리자가 없을 때도
 * BE 가 404(USER_NOT_FOUND)를 주므로, 오류 코드가 실린 404 는 그 코드로 안내한다(user-service #47).
 */
class PasswordResetNotReadyError extends Error {}

const usePasswordReset = (adminUserId: string) =>
  useMutation({
    mutationFn: async (newPassword: string) => {
      const path = `/admin/v1/admins/${adminUserId}/password`;
      const body = { newPassword };
      const res = await adminFetch(path, {
        method: 'PUT',
        headers: { 'Idempotency-Key': idempotencyKeyFor('PUT', path, { at: Date.now() }) },
        body: JSON.stringify(body),
      });
      if (res.status === 405) throw new PasswordResetNotReadyError('not ready');
      if (!res.ok) {
        const err = await errorFromResponse(res);
        if (res.status === 404 && !(err instanceof ApiClientError && err.code === 'USER_NOT_FOUND')) {
          throw new PasswordResetNotReadyError('not ready');
        }
        throw err;
      }
    },
  });

/**
 * QA-424 — 최고관리자가 다른 관리자 계정의 비밀번호를 새 값으로 초기화한다(관리자 수정 화면).
 * 대상 관리자의 로그인 세션은 BE 가 끊는다(전달 문서). 본인 계정은 [내 정보]의 비밀번호 변경을 쓴다.
 */
export const AdminPasswordResetSection = ({ adminUserId, adminName }: { adminUserId: string; adminName: string }) => {
  const { t } = useTranslation('common');
  const reset = usePasswordReset(adminUserId);
  const [open, setOpen] = useState(false);
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [touched, setTouched] = useState(false);

  const policyOk = isValidAdminPassword(next);
  const matchOk = next === confirm;
  const canSubmit = policyOk && matchOk && !reset.isPending;

  const close = () => {
    setOpen(false);
    setNext('');
    setConfirm('');
    setTouched(false);
    reset.reset();
  };

  const submit = () => {
    setTouched(true);
    if (!policyOk || !matchOk) return;
    reset.mutate(next, {
      onSuccess: () => {
        toast.success(
          t('admin.accounts.passwordReset.done', {
            defaultValue: '{{name}} 님의 비밀번호를 초기화했습니다. 새 비밀번호로 다시 로그인해야 합니다.',
            name: adminName,
          }),
        );
        close();
      },
    });
  };

  // user-service #47 — 400/404 는 error.code 별로 안내한다.
  const errorCode = reset.error instanceof ApiClientError ? String(reset.error.code) : null;
  const serverError = !reset.error
    ? null
    : reset.error instanceof PasswordResetNotReadyError
      ? t('admin.accounts.passwordReset.notReady', { defaultValue: '비밀번호 초기화는 서버(BE) 반영 후 사용할 수 있습니다.' })
      : errorCode === 'USER_PASSWORD_POLICY_FAILED'
        ? t('admin.accounts.passwordReset.errPolicy', {
            defaultValue: '비밀번호 규칙에 맞지 않습니다. 10자 이상, 영문·숫자·특수문자 중 3가지를 섞어 주세요.',
          })
        : errorCode === 'USER_PASSWORD_REUSED'
          ? t('admin.accounts.passwordReset.errReused', { defaultValue: '지금 비밀번호와 다른 비밀번호를 입력해 주세요.' })
          : errorCode === 'COMMON_VALIDATION_FAILED'
            ? t('admin.accounts.passwordReset.errSelf', {
                defaultValue: '본인 계정은 여기서 초기화할 수 없습니다. [내 정보]에서 비밀번호를 바꿔 주세요.',
              })
            : errorCode === 'USER_NOT_FOUND'
              ? t('admin.accounts.passwordReset.errNotFound', { defaultValue: '관리자 계정을 찾을 수 없습니다. 목록에서 다시 열어 주세요.' })
              : friendlyMessage(reset.error, t('admin.accounts.passwordReset.fail', { defaultValue: '비밀번호를 초기화하지 못했습니다.' }));

  return (
    <SectionCard title={t('admin.accounts.passwordReset.title', { defaultValue: '비밀번호 초기화' })}>
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm" style={{ color: 'var(--content-muted)' }}>
          {t('admin.accounts.passwordReset.description', {
            defaultValue: '이 관리자의 비밀번호를 새로 정합니다. 정한 비밀번호는 해당 관리자에게 따로 전달해 주세요.',
          })}
        </p>
        <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="admin-password-reset-open">
          {t('admin.accounts.passwordReset.open', { defaultValue: '비밀번호 초기화' })}
        </Button>
      </div>

      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.accounts.passwordReset.title', { defaultValue: '비밀번호 초기화' })}</DialogTitle>
            <DialogDescription>
              {t('admin.accounts.passwordReset.dialogDescription', {
                defaultValue: '{{name}} 님의 비밀번호를 아래 값으로 바꿉니다. 로그인 중이면 로그아웃됩니다.',
                name: adminName,
              })}
            </DialogDescription>
          </DialogHeader>
          <form
            className="flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            <label className="flex flex-col gap-1 text-sm">
              {t('admin.accounts.passwordReset.new', { defaultValue: '새 비밀번호' })}
              <Input
                type="password"
                autoComplete="new-password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                data-testid="admin-password-reset-new"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              {t('admin.accounts.passwordReset.confirm', { defaultValue: '새 비밀번호 확인' })}
              <Input
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="admin-password-reset-confirm"
              />
            </label>
            <p className="text-[12px]" style={{ color: touched && !policyOk ? 'var(--signal-danger)' : 'var(--content-muted)' }}>
              {t('admin.accounts.passwordReset.policy', { defaultValue: '10자 이상, 영문·숫자·특수문자 중 3가지를 섞어 주세요.' })}
            </p>
            {touched && policyOk && !matchOk ? (
              <p className="text-[12px]" style={{ color: 'var(--signal-danger)' }} role="alert">
                {t('admin.accounts.passwordReset.mismatch', { defaultValue: '새 비밀번호와 확인이 다릅니다.' })}
              </p>
            ) : null}
            {serverError ? (
              <p className="text-[12px]" style={{ color: 'var(--signal-danger)' }} role="alert" data-testid="admin-password-reset-error">
                {serverError}
              </p>
            ) : null}
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="ghost" size="sm">
                  {t('cms.cancel', { defaultValue: '취소' })}
                </Button>
              </DialogClose>
              <Button type="submit" size="sm" disabled={reset.isPending || (touched && !canSubmit)} data-testid="admin-password-reset-submit">
                {reset.isPending
                  ? t('admin.accounts.passwordReset.submitting', { defaultValue: '초기화 중…' })
                  : t('admin.accounts.passwordReset.submit', { defaultValue: '초기화' })}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
};
